import { useEffect, useMemo, useRef, useState } from "react";

import { useNavigate, useSearchParams } from "react-router-dom";

import dayjs from "dayjs";
import { ArrowLeft, FileDown, Loader2 } from "lucide-react";

import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { useAccounts } from "~/hooks/contabilidad/useAccounts";
import { useCompanies } from "~/hooks/contabilidad/useCompanies";
import { usePublishedMoveLines } from "~/hooks/contabilidad/useDiarioSummary";
import { useLibroMayor } from "~/hooks/contabilidad/useLibroMayor";
import { useToast } from "~/hooks/use-toast";
import { diarioService } from "~/services/contabilidad/diarioService";
import { exportLibroMayorDetailToExcel } from "~/services/contabilidad/libroMayorExportService";

import { AccountCodeCombobox } from "./components/AccountCodeCombobox";
import { LibroMayorDetailDialog } from "./components/LibroMayorDetailDialog";
import { LibroMayorTable } from "./components/LibroMayorTable";
import { MonthRangeSelector } from "./components/MonthRangeSelector";

import type { LibroMayorLine } from "~/types/contabilidad";

const ALIACE_SPA_ID = "809d3197-9371-4cbb-b544-aadbd29940d6";

export default function LibroMayorPage() {
  const navigate = useNavigate();
  const { toast } = useToast();

  // Optional deep-link params (e.g. from the Reconciliación CxP report):
  // ?company=<uuid>&account=<code>&month=YYYY-MM&detail=credit|debit
  const [searchParams] = useSearchParams();
  const initialMonth = searchParams.get("month");
  const initialAccount = searchParams.get("account") ?? "";

  const [dateFrom, setDateFrom] = useState(() =>
    (initialMonth ? dayjs(initialMonth) : dayjs())
      .startOf("month")
      .format("YYYY-MM-DD")
  );
  const [dateTo, setDateTo] = useState(() =>
    (initialMonth ? dayjs(initialMonth) : dayjs())
      .endOf("month")
      .format("YYYY-MM-DD")
  );
  const [companyFilter, setCompanyFilter] = useState<string>(
    searchParams.get("company") ?? ""
  );
  const [accountFrom, setAccountFrom] = useState<string>(initialAccount);
  const [accountTo, setAccountTo] = useState<string>(initialAccount);

  // Detail dialog state
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedDetailAccount, setSelectedDetailAccount] =
    useState<LibroMayorLine | null>(null);
  const [selectedDetailType, setSelectedDetailType] = useState<
    "debit" | "credit"
  >("debit");
  const [isExporting, setIsExporting] = useState(false);

  const { data: companies = [] } = useCompanies();
  const { data: accounts = [] } = useAccounts();

  // Normalize the account-code range. Order of selection doesn't matter, and a
  // single selected account acts as a single-account filter (both bounds = that
  // code) rather than an open-ended "everything up to X".
  const chosenCodes = [accountFrom, accountTo].filter(Boolean).sort();
  const accountCodeFrom = chosenCodes[0] ?? "";
  const accountCodeTo = chosenCodes[chosenCodes.length - 1] ?? "";

  const filters = useMemo(
    () => ({
      dateFrom,
      dateTo,
      companyId: companyFilter,
      accountCodeFrom: accountCodeFrom || undefined,
      accountCodeTo: accountCodeTo || undefined,
    }),
    [dateFrom, dateTo, companyFilter, accountCodeFrom, accountCodeTo]
  );

  const { data: lines = [], isLoading } = useLibroMayor(filters);

  // Active accounts for the selected company (deduped by code), sorted by code.
  const accountOptions = useMemo(() => {
    if (!companyFilter) return [];
    const byCode = new Map<string, (typeof accounts)[number]>();
    for (const a of accounts) {
      if (a.company_id !== companyFilter || !a.is_active) continue;
      if (!byCode.has(a.code)) byCode.set(a.code, a);
    }
    return Array.from(byCode.values()).sort((x, y) =>
      x.code.localeCompare(y.code)
    );
  }, [accounts, companyFilter]);

  // Fetch detail lines only while a detail account is selected (keeps the
  // dialog's query from pulling the whole ledger on page load).
  const { data: detailLines = [], isLoading: isLoadingDetail } =
    usePublishedMoveLines({
      dateFrom: selectedDetailAccount ? dateFrom : "",
      dateTo: selectedDetailAccount ? dateTo : "",
      companyId: companyFilter,
      accountId: selectedDetailAccount?.account_id,
    });

  const handleDetailClick = (line: LibroMayorLine, type: "debit" | "credit") => {
    setSelectedDetailAccount(line);
    setSelectedDetailType(type);
    setDetailDialogOpen(true);
  };

  // Reset account range when company changes
  const handleCompanyChange = (value: string) => {
    setCompanyFilter(value);
    setAccountFrom("");
    setAccountTo("");
  };

  const accountRangeLabel = useMemo(() => {
    if (!accountCodeFrom && !accountCodeTo) return undefined;
    return `${accountCodeFrom || "(inicio)"} - ${accountCodeTo || "(fin)"}`;
  }, [accountCodeFrom, accountCodeTo]);

  const periodLabel = `${dayjs(dateFrom).format("DD/MM/YYYY")} - ${dayjs(
    dateTo
  ).format("DD/MM/YYYY")}`;

  // Handle export to Excel
  const handleExport = async () => {
    if (!companyFilter) return;

    setIsExporting(true);
    try {
      const exportLines = await diarioService.getPublishedMoveLines({
        dateFrom,
        dateTo,
        companyId: companyFilter,
        accountCodeFrom: accountCodeFrom || undefined,
        accountCodeTo: accountCodeTo || undefined,
      });

      if (exportLines.length === 0) {
        toast({
          title: "Sin datos",
          description:
            "No hay movimientos para exportar en el periodo seleccionado.",
          variant: "destructive",
        });
        return;
      }

      const selectedCompany = companies.find((c) => c.id === companyFilter);

      exportLibroMayorDetailToExcel({
        summaryLines: lines,
        detailLines: exportLines,
        period: periodLabel,
        companyName: selectedCompany?.name || "",
        accountName: accountRangeLabel,
      });

      toast({
        title: "Reporte exportado",
        description: "El archivo Excel se ha descargado correctamente.",
      });
    } catch (error) {
      console.error("Error exporting Libro Mayor detail:", error);
      toast({
        title: "Error",
        description: "No se pudo exportar el reporte.",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  // Set default company to Aliace SPA
  useEffect(() => {
    if (companies.length > 0 && !companyFilter) {
      const aliaceSpa = companies.find((c) => c.id === ALIACE_SPA_ID);
      if (aliaceSpa) {
        setCompanyFilter(ALIACE_SPA_ID);
      } else if (companies.length === 1) {
        setCompanyFilter(companies[0].id);
      }
    }
  }, [companies]);

  // Deep-link: ?detail=credit|debit auto-opens the "Detalle de Débitos/Créditos"
  // dialog for the linked account once its summary line is loaded (one-time).
  const autoOpenedDetail = useRef(false);
  useEffect(() => {
    if (autoOpenedDetail.current) return;
    const detail = searchParams.get("detail");
    if (detail !== "credit" && detail !== "debit") return;
    if (lines.length === 0) return;
    const code = searchParams.get("account");
    const line =
      (code && lines.find((l) => l.account_code === code)) || lines[0];
    if (line) {
      autoOpenedDetail.current = true;
      setSelectedDetailAccount(line);
      setSelectedDetailType(detail);
      setDetailDialogOpen(true);
    }
  }, [searchParams, lines]);

  return (
    <div className="container mx-auto w-[80vw] space-y-6 py-6">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/contabilidad/reportes")}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold">Libro Mayor</h1>
          <p className="text-sm text-muted-foreground">
            Resumen de movimientos por cuenta contable
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col flex-wrap gap-3 sm:flex-row sm:items-end">
            <MonthRangeSelector
              dateFrom={dateFrom}
              dateTo={dateTo}
              onChange={(next) => {
                setDateFrom(next.dateFrom);
                setDateTo(next.dateTo);
              }}
            />
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="lm-desde" className="text-sm font-medium">
                Desde
              </Label>
              <Input
                id="lm-desde"
                type="date"
                value={dateFrom}
                max={dateTo}
                onChange={(e) => setDateFrom(e.target.value)}
                className="h-9 w-44 rounded-xl"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="lm-hasta" className="text-sm font-medium">
                Hasta
              </Label>
              <Input
                id="lm-hasta"
                type="date"
                value={dateTo}
                min={dateFrom}
                onChange={(e) => setDateTo(e.target.value)}
                className="h-9 w-44 rounded-xl"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label className="text-sm font-medium">
                Empresa <span className="text-red-500">*</span>
              </Label>
              <Select value={companyFilter} onValueChange={handleCompanyChange}>
                <SelectTrigger className="h-9 w-[200px] rounded-xl">
                  <SelectValue placeholder="Seleccionar empresa" />
                </SelectTrigger>
                <SelectContent>
                  {companies.map((company) => (
                    <SelectItem key={company.id} value={company.id}>
                      {company.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label className="text-sm font-medium">Cuenta desde</Label>
              <AccountCodeCombobox
                accounts={accountOptions}
                value={accountFrom}
                onChange={setAccountFrom}
                disabled={!companyFilter}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label className="text-sm font-medium">Cuenta hasta</Label>
              <AccountCodeCombobox
                accounts={accountOptions}
                value={accountTo}
                onChange={setAccountTo}
                disabled={!companyFilter}
              />
            </div>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            Selecciona una sola cuenta para ver únicamente esa cuenta, o una en
            “Cuenta desde” y otra en “Cuenta hasta” para un rango o grupo (ej.
            1202001 a 1202014). Deja ambas en “Todas las cuentas” para incluir
            todo el plan de cuentas.
          </p>
        </CardHeader>
      </Card>

      {!companyFilter ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <p className="text-muted-foreground">
              Selecciona una empresa para ver el Libro Mayor.
            </p>
            <p className="text-sm text-muted-foreground">
              La empresa es un campo requerido para este reporte.
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Detalle por Cuenta</CardTitle>
                <p className="text-sm text-muted-foreground">
                  {lines.length} cuentas con movimientos
                </p>
              </div>
              <Button onClick={handleExport} disabled={isExporting}>
                {isExporting ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <FileDown className="mr-2 h-4 w-4" />
                )}
                Exportar detalle
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <LibroMayorTable
              lines={lines}
              isLoading={isLoading}
              onDetailClick={handleDetailClick}
            />
          </CardContent>
        </Card>
      )}

      {selectedDetailAccount && (
        <LibroMayorDetailDialog
          open={detailDialogOpen}
          onOpenChange={setDetailDialogOpen}
          accountCode={selectedDetailAccount.account_code}
          accountName={selectedDetailAccount.account_name}
          type={selectedDetailType}
          lines={detailLines}
          isLoading={isLoadingDetail}
        />
      )}
    </div>
  );
}
